import React, { Component } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import LandingPage from "./components/LandingPage/landingPage";
import "./App.css";
import ProtectedRoute from "./components/common/protectedRoute";
import ResetPassword from "./components/forgotPassword";
import Logout from "./components/logout";
import Login from "./components/Login/login";
import NotFound from "./components/notFound";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { NavBar } from "./components/NavBar/navBar";
import SideDrawer from "./components/SideDrawer/SideDrawer";
import { BackDrop } from "./components/BackDrop/BackDrop";
import LeftAlertDrawer from "./components/LeftAlerts/LeftAlertDrawer";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import Clients from "./components/Clients/clients";
import User from "./components/UserSettings/user";
import auth from "./services/authService";
import {
  archiveClientAttest,
  getAttestClients,
  submitClientAttest,
} from "./services/complianceAPIServices";

// Session security constants
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const INACTIVITY_WARNING_MS = 13 * 60 * 1000; // 13 minutes (warning appears)
const WARNING_DURATION_MS = 2 * 60 * 1000; // 2-minute warning window
const TOKEN_REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // refresh tokens 2 min before expiry

export default class App extends Component {
  _isMounted = false;
  fpList = [];
  fpAdvList = [];

  // Inactivity tracking
  lastActivityTime = Date.now();
  _inactivityWarningVisible = false;
  _warningStartTime = null;
  _isRefreshing = false;
  _lastActivityUpdate = 0;

  constructor(props) {
    super(props);
    this.position = 0;
    this.scrollHeight = "100vh";
    this.state = {
      session: undefined,
      background: "bg",
      sideDrawerOpen: false,
      leftAlertDrawerOpen: false,
      sessionExpireNotificaiton: false,
      alertMessage: "",
      alertButtonMessage: "",
      confirmFunction: null,
      groups: [],
      financialProfessionals: [],
      clients: [],
      serverRequests: {},
      currentFP: "",
      updatingFP: false,
      gettingClients: false,
      scroll: 0,
      warningRemainingSeconds: 0,
    };
  }

  handleScroll = (event) => {
    const { scrollTop, scrollHeight } = event.target;
    this.position = scrollTop;
    this.scrollHeight = scrollHeight;
  };

  GetScrollPosition = () => {
    return this.position;
  };

  GetScrollHeight = () => {
    return this.scrollHeight;
  };

  componentDidMount = async () => {
    this._isMounted = true;
    await this.getCurrentSession();
    this.handleBackgroundUpdate();
    if (this.state.session) {
      const userGroups = await this.getCurrentUserSessionGroups();
      console.log("[DEBUG componentDidMount] userGroups:", userGroups);
      console.log("[DEBUG componentDidMount] includes compliance?", userGroups.includes("compliance"));
      console.log("[DEBUG componentDidMount] includes system_admin?", userGroups.includes("system_admin"));
      // Use returned groups directly to avoid setState race condition
      if (
        userGroups.includes("compliance") ||
        userGroups.includes("system_admin")
      ) {
        // Elevated users: load FP list only, wait for advisor selection before loading clients
        console.log("[DEBUG componentDidMount] User IS elevated, loading FP list only");
        await this.getFinancialProfessionals();
      } else {
        console.log("[DEBUG componentDidMount] User is NOT elevated, loading clients via access-accounts");
        this.getClients();
      }

      // Start version polling (every 5 minutes + on tab focus)
      this._checkVersion = async () => {
        if (!this._isMounted) return;
        try {
          const res = await fetch(`/version.json?t=${Date.now()}`);
          if (!res.ok) return;
          const { version } = await res.json();
          if (version && version !== window.APP_VERSION) {
            console.info(
              `New version detected (${window.APP_VERSION} → ${version}). Reloading.`
            );
            window.location.reload();
          }
        } catch (err) {
          console.warn("Version check failed:", err);
        }
      };
      this.versionCheckInterval = setInterval(this._checkVersion, 5 * 60 * 1000);
      this._onVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          this._checkVersion();
        }
      };
      document.addEventListener("visibilitychange", this._onVisibilityChange);

      // Start session & inactivity monitoring
      this.lastActivityTime = Date.now();
      this.sessionCheckInterval = setInterval(() => {
        if (!this._isMounted) return;

        const now = Date.now();
        const inactivityDuration = now - this.lastActivityTime;
        const tokenExp = auth.getTokenExpiration(); // read-only, no refresh
        const tokenExpMs = tokenExp ? tokenExp * 1000 : null;

        // CASE 1: Warning is visible — manage countdown
        if (this._inactivityWarningVisible) {
          const elapsed = now - this._warningStartTime;
          const remaining = WARNING_DURATION_MS - elapsed;
          if (remaining <= 0) {
            this._inactivityWarningVisible = false;
            clearInterval(this.sessionCheckInterval);
            window.location = "/logout";
            return;
          }
          this.setState({
            warningRemainingSeconds: Math.ceil(remaining / 1000),
          });
          return;
        }

        // CASE 2: Inactivity threshold reached — show warning
        if (inactivityDuration >= INACTIVITY_WARNING_MS) {
          this._inactivityWarningVisible = true;
          this._warningStartTime =
            now - (inactivityDuration - INACTIVITY_WARNING_MS);
          const remaining = INACTIVITY_TIMEOUT_MS - inactivityDuration;
          const remainingSeconds = Math.max(
            0,
            Math.ceil(remaining / 1000)
          );
          this.setState({
            sessionExpireNotificaiton: true,
            alertMessage: "You will be logged out due to inactivity",
            alertButtonMessage: "Stay signed in",
            confirmFunction: this.confirmAlert,
            leftAlertDrawerOpen: true,
            warningRemainingSeconds: remainingSeconds,
          });
          return;
        }

        // CASE 3: Token about to expire and user is active — silent refresh
        if (
          !this._isRefreshing &&
          tokenExpMs &&
          tokenExpMs - now < TOKEN_REFRESH_THRESHOLD_MS &&
          tokenExpMs - now > 0
        ) {
          this._isRefreshing = true;
          fetchAuthSession({ forceRefresh: true })
            .then(() => {
              this._isRefreshing = false;
            })
            .catch(() => {
              this._isRefreshing = false;
              clearInterval(this.sessionCheckInterval);
              window.location = "/logout";
            });
          return;
        }

        // CASE 4: Token already expired — logout
        if (tokenExpMs && now >= tokenExpMs) {
          clearInterval(this.sessionCheckInterval);
          window.location = "/logout";
          return;
        }

        // CASE 5: No valid token found — logout
        if (!tokenExpMs && this.state.session) {
          clearInterval(this.sessionCheckInterval);
          window.location = "/logout";
          return;
        }
      }, 1000);

      // Inactivity tracking — DOM event listeners
      this._handleUserActivity = () => {
        if (this._inactivityWarningVisible) return;
        const now = Date.now();
        if (now - this._lastActivityUpdate < 30000) return;
        this._lastActivityUpdate = now;
        this.lastActivityTime = now;
      };

      const activityEvents = [
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];
      activityEvents.forEach((event) => {
        window.addEventListener(event, this._handleUserActivity, {
          passive: true,
        });
      });

      // Tab visibility change — catch up on inactivity when returning
      this._handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") return;
        if (this._inactivityWarningVisible) return;

        const now = Date.now();
        const inactivityDuration = now - this.lastActivityTime;

        if (inactivityDuration >= INACTIVITY_TIMEOUT_MS) {
          clearInterval(this.sessionCheckInterval);
          window.location = "/logout";
        } else if (inactivityDuration >= INACTIVITY_WARNING_MS) {
          this._inactivityWarningVisible = true;
          this._warningStartTime =
            now - (inactivityDuration - INACTIVITY_WARNING_MS);
          const remaining = INACTIVITY_TIMEOUT_MS - inactivityDuration;
          this.setState({
            sessionExpireNotificaiton: true,
            alertMessage: "You will be logged out due to inactivity",
            alertButtonMessage: "Stay signed in",
            confirmFunction: this.confirmAlert,
            leftAlertDrawerOpen: true,
            warningRemainingSeconds: Math.max(
              0,
              Math.ceil(remaining / 1000)
            ),
          });
        }
      };
      document.addEventListener(
        "visibilitychange",
        this._handleVisibilityChange
      );
    }
  };

  componentWillUnmount = () => {
    this._isMounted = false;
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
    }
    if (this._onVisibilityChange) {
      document.removeEventListener("visibilitychange", this._onVisibilityChange);
    }
    if (this._handleVisibilityChange) {
      document.removeEventListener(
        "visibilitychange",
        this._handleVisibilityChange
      );
    }
    if (this._handleUserActivity) {
      const activityEvents = [
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];
      activityEvents.forEach((event) => {
        window.removeEventListener(event, this._handleUserActivity);
      });
    }
  };

  alreadyServerRequest(str) {
    let empty = false;
    try {
      empty = this.state.serverRequests[str];
    } catch (e) {
      return false;
    }
    return empty;
  }

  toggleServerRequest = (request) => {
    if (typeof request === "string" && !this.alreadyServerRequest(request)) {
      this.setState((prevState) => {
        return {
          serverRequests: { ...prevState.serverRequests, [request]: true },
        };
      });
    } else {
      let serverRequests = this.state.serverRequests;
      delete serverRequests[request];
      this.setState({ serverRequests: serverRequests });
    }
  };

  getClients = async (financial_professional = "") => {
    console.log("[DEBUG getClients] Starting with FP:", financial_professional);
    this.setState({ clients: [], gettingClients: true });
    await this.handleFPUpdating(true);
    this.toggleServerRequest("clients");

    // Paginated fetch: Lambda returns up to 1000 items per call with lastEvaluatedKey
    let allClients = [];
    let lastEvaluatedKey = "";
    let page = 0;

    do {
      page++;
      const attestClientsResponse = await getAttestClients(
        financial_professional,
        lastEvaluatedKey
      );
      console.log(`[DEBUG getClients] Page ${page} raw response:`, attestClientsResponse);
      const pageClients = JSON.parse(attestClientsResponse.body);
      allClients.push(...pageClients);
      lastEvaluatedKey = attestClientsResponse.lastEvaluatedKey || "";
      console.log(`[DEBUG getClients] Page ${page}: ${pageClients.length} items, total so far: ${allClients.length}, hasMore: ${!!lastEvaluatedKey}`);
    } while (lastEvaluatedKey);

    // Remove duplicates
    const beforeDedup = allClients.length;
    allClients = this.removeJsonArrayDuplicates(allClients);
    console.log("[DEBUG getClients] After dedup:", allClients.length, "(removed", beforeDedup - allClients.length, "duplicates)");

    this.setState({
      clients: allClients,
      currentFP: financial_professional,
      gettingClients: false,
    });
    this.toggleServerRequest("clients");
    await this.handleFPUpdating(false);
  };

  /**
   * Load clients for multiple financial professionals
   * @param {Array} fpList - Array of financial professional identifiers
   */
  getClientsForMultipleFPs = async (fpList = []) => {
    if (!fpList || fpList.length === 0) {
      return;
    }

    this.setState({ clients: [], gettingClients: true });
    await this.handleFPUpdating(true);
    this.toggleServerRequest("clients");

    // Map selected individual names back to unique full composite group-ids
    // (e.g. "Andrew Kern CFP®" → "Andrew Kern CFP®;Liana Freeland")
    const fullGroupIds = new Set();
    fpList.forEach((fp) => {
      const groupIds = this.fpToGroupIds?.get(fp);
      if (groupIds) {
        groupIds.forEach((gid) => fullGroupIds.add(gid));
      } else {
        fullGroupIds.add(fp); // fallback: use as-is
      }
    });
    const groupIdList = [...fullGroupIds];

    // Helper function to fetch clients for a single group-id
    const fetchClientsForFP = async (groupId) => {
      const response = await getAttestClients(groupId);
      return JSON.parse(response.body);
    };

    // Fetch in batches to avoid Cognito rate-limit throttling
    const BATCH_SIZE = 10;
    const clientArrays = [];
    for (let i = 0; i < groupIdList.length; i += BATCH_SIZE) {
      const batch = groupIdList.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(batch.map(fetchClientsForFP));
      clientArrays.push(...batchResults);
    }

    // Flatten and combine all results
    let allClients = clientArrays.flat();

    // Remove duplicates from combined results
    allClients = this.removeJsonArrayDuplicates(allClients);

    this.setState({
      clients: allClients,
      currentFP: fpList.length === 1 ? fpList[0] : "Multiple",
      gettingClients: false,
    });

    this.toggleServerRequest("clients");
    await this.handleFPUpdating(false);
  };

  removeJsonArrayDuplicates = (arr) => {
    const seen = new Set();
    return arr.filter((item) => {
      // Deduplicate by DynamoDB primary key (client-name + group-id)
      // JSON.stringify can fail to dedup when attribute key order differs across scans
      const key = `${item["client-name"]}|${item["group-id"]}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  handleFPUpdating = async (bool) => {
    this.setState({ updatingFP: bool });
  };

  getFinancialProfessionals = async () => {
    console.log("[DEBUG getFPs] Starting request for all FPs");
    this.toggleServerRequest("FPs");
    // Lambda now returns complete FP list in single response (no pagination needed)
    const attestFPsResponse = await getAttestClients("all");
    console.log("[DEBUG getFPs] Raw response:", attestFPsResponse);
    const attestFPs = JSON.parse(attestFPsResponse.body);
    console.log("[DEBUG getFPs] Parsed FP tuples count:", attestFPs.length);
    console.log("[DEBUG getFPs] Sample tuples (first 5):", attestFPs.slice(0, 5));
    console.log("[DEBUG getFPs] Sample tuples (last 5):", attestFPs.slice(-5));

    // Split composite group-ids (e.g. "A;B") into individual dropdown entries
    // and build reverse mapping so API calls can resolve back to full group-ids
    const fpToGroupIds = new Map(); // individual name → Set<full composite group-id>
    const allIndividualFPs = new Set();

    attestFPs.forEach((fp) => {
      const fullGroupId = fp[0];
      const parts = fullGroupId.split(";").map((p) => p.trim()).filter((p) => p);
      parts.forEach((part) => {
        allIndividualFPs.add(part);
        if (!fpToGroupIds.has(part)) fpToGroupIds.set(part, new Set());
        fpToGroupIds.get(part).add(fullGroupId);
      });
    });

    this.fpToGroupIds = fpToGroupIds;
    const uniqueFPs = [...allIndividualFPs].sort();
    console.log("[DEBUG getFPs] Individual FP entries:", uniqueFPs.length, "(from", attestFPs.length, "group-ids)");

    this.fpList = uniqueFPs;
    this.setState({ financialProfessionals: uniqueFPs });
    this.toggleServerRequest("FPs");
  };

  getCurrentUserSessionGroups = async () => {
    try {
      const session = await fetchAuthSession();
      console.log("[DEBUG getCurrentUserSessionGroups] session.tokens:", session.tokens);
      console.log("[DEBUG getCurrentUserSessionGroups] idToken payload:", session.tokens?.idToken?.payload);
      console.log("[DEBUG getCurrentUserSessionGroups] raw cognito:groups:", session.tokens?.idToken?.payload["cognito:groups"]);
      let currentUserGroups = [];
      if (session.tokens?.idToken?.payload["cognito:groups"]) {
        currentUserGroups = session.tokens.idToken.payload["cognito:groups"].map(
          (g) => g.toLowerCase()
        );
      }
      console.log("[DEBUG getCurrentUserSessionGroups] final groups (lowercase):", currentUserGroups);
      if (this._isMounted) {
        this.setState({ groups: currentUserGroups });
      }
      return currentUserGroups; // Return groups for immediate use
    } catch (error) {
      console.log("Error getting user session groups:", error);
      if (this._isMounted) {
        this.setState({ groups: [] });
      }
      return []; // Return empty array on error
    }
  };

  getCurrentSession = async () => {
    try {
      const session = await fetchAuthSession();
      const accessExp = session?.tokens?.accessToken?.payload?.exp;
      const idExp = session?.tokens?.idToken?.payload?.exp;
      const now = Date.now() / 1000;

      if (accessExp && idExp && accessExp > now && idExp > now && this._isMounted) {
        this.setState({ session: true });
      } else if (this._isMounted) {
        this.setState({ session: false });
      }
    } catch {
      if (this._isMounted) {
        this.setState({ session: false });
      }
    }
  };

  handleBackgroundUpdate = () => {
    const currentUrl = window.location.pathname;
    if (!currentUrl.includes("log")) {
      this.setState({ background: "page-bg" });
    }
  };

  drawerToggleClickHandler = () => {
    this.setState((prevState) => {
      return { sideDrawerOpen: !prevState.sideDrawerOpen };
    });
  };

  backdropClickHandler = () => {
    this.setState({ sideDrawerOpen: false });
  };

  handleClientUpdate = (client, updateType, update) => {
    let clients = this.state.clients;
    const clientIndex = clients.indexOf(client);
    clients[clientIndex][updateType] = update[updateType];
    this.setState({ clients: clients });
  };

  handleClientArchive = async (client) => {
    if (
      window.confirm(
        `Are you sure you wish to archive ${client["client-name"]}`
      )
    ) {
      this.handleClientServerUpdate(client, true);
      try {
        let response = await archiveClientAttest(client);
        if (response.hasOwnProperty("statusCode")) {
          if (response.statusCode === "500") {
            alert(
              "Error communicating with server please try again later or contact support."
            );
            this.handleClientServerUpdate(client, false);
            return;
          }
        }
        // Use filter to create a new array reference so the memoization cache
        // in Clients.getPagedData is properly invalidated.
        const updatedClients = this.state.clients.filter((c) => c !== client);
        this.setState({ clients: updatedClients });
      } catch (error) {
        console.error("Error archiving client:", error);
        alert(
          "Error communicating with server please try again later or contact support."
        );
        this.handleClientServerUpdate(client, false);
      }
    }
  };

  submitClientUpdate = async (client, subChange = "") => {
    this.handleClientServerUpdate(client, true);
    await submitClientAttest(client, subChange);
    this.handleClientServerUpdate(client, false);
  };

  handleAttestSubmit = async (client, status = "Submitted") => {
    let clients = this.state.clients;
    let clientAttestDate = client["review-date"].includes("/")
      ? client["review-date"].split("/")
      : client["review-date"].split("-");
    let clientFound = clients.find(
      (cl) =>
        cl["client-name"] === client["client-name"] &&
        cl["group-id"] === client["group-id"]
    );
    if (!clientFound) {
      return;
    }
    this.handleClientServerUpdate(clientFound, true);
    const clientIndex = clients.indexOf(clientFound);
    // Update client
    if (
      new Date(clientFound["review-date"]).getTime()
        ? new Date(clientFound["review-date"]).getTime()
        : 0 < new Date(client["review-date"]).getTime()
    ) {
      clientFound["attestation-status"] = status;
      clientFound["reviewed-this-year"] = client["reviewed-this-year"];
      clientFound["review-date"] = client["review-date"].includes("/")
        ? new Date(
            clientAttestDate[2], // Year
            clientAttestDate[0] - 1, // MonthIndex (0-11)
            clientAttestDate[1] // Day
          ).toLocaleDateString()
        : new Date(
            clientAttestDate[0], // Year
            clientAttestDate[1] - 1, // MonthIndex (0-11)
            clientAttestDate[2] // Day
          ).toLocaleDateString();
      clientFound["review-structure"] = client["review-structure"];
      clientFound["account-substantial-change"] =
        client["account-substantial-change"];
      clientFound["account-r-t-a-changes"] = client["account-r-t-a-changes"];
      clientFound["reg-b-i-changes"] = client["reg-b-i-changes"];
    }
    let currentUser = await getCurrentUser();
    let accountObject = {};
    client.accounts.forEach((account) => {
      account = account.split("_").slice(1);
      let accountType = account[2];
      accountObject[accountType] = [];
    });
    client.accounts.forEach((account) => {
      account = account.split("_").slice(1);
      let accountName = account[0];
      let groupId = account[1];
      let accountType = account[2];
      let accountNumber = account[3];
      clients.forEach((cl) => {
        if (accountName === cl["client-name"] && groupId === cl["group-id"]) {
          accountObject[accountType].push({
            account: accountNumber,
            submitted: true,
          });
        }
      });
    });
    clientFound.history.unshift({
      "attest-date-time": `${new Date().toLocaleString()}`,
      "review-date": client["review-date"],
      status: status,
      "submitted-by": currentUser.username,
      accounts: accountObject,
    });
    clients[clientIndex] = clientFound;
    await submitClientAttest(clientFound);
    this.setState({ clients: clients });
    this.handleClientServerUpdate(clientFound, false);
  };

  handleClientAccountSubmissions = async (client, currPrev = "Current") => {
    let clients = [...this.state.clients];
    let accountSet = new Set();
    client.accounts.forEach((account) => {
      account = account.split("_").slice(1);
      let accountName = account[0];
      let groupId = account[1];
      let accountType = account[2];
      let accountNumber = account[3];
      let fullySubmitted = false;
      clients.forEach((c) => {
        if (accountName === c["client-name"] && groupId === c["group-id"]) {
          try {
            c.accounts[currPrev][accountType].forEach((accountItem) => {
              if (accountItem.account === accountNumber) {
                clients[clients.indexOf(c)].accounts[currPrev][accountType][
                  c.accounts[currPrev][accountType].indexOf(accountItem)
                ].submitted = "true";
              }
            });
            let trueCount = 0;
            let totalCount = 0;
            Object.keys(c.accounts[currPrev]).forEach((key) => {
              totalCount = totalCount + c.accounts[currPrev][key].length;
              c.accounts[currPrev][key].forEach((accountItem) => {
                if (
                  clients[clients.indexOf(c)].accounts[currPrev][key][
                    c.accounts[currPrev][key].indexOf(accountItem)
                  ].submitted === "true"
                ) {
                  trueCount = trueCount + 1;
                }
              });
            });
            if (trueCount === totalCount && trueCount > 0) {
              fullySubmitted = true;
            }
          } catch (error) {
            return;
          }
        }
      });
      if (fullySubmitted) {
        accountSet.delete(
          JSON.stringify({
            accountName: accountName,
            groupId: groupId,
            fullySubmitted: false,
          })
        );
      }
      accountSet.add(
        JSON.stringify({
          accountName: accountName,
          groupId: groupId,
          fullySubmitted: fullySubmitted,
        })
      );
    });
    this.setState({ clients: clients });
    // multi submit
    accountSet.forEach(async (a) => {
      a = JSON.parse(a);
      let tempClient = client;
      tempClient["client-name"] = a.accountName;
      tempClient["group-id"] = a.groupId;
      await this.handleAttestSubmit(
        tempClient,
        a.fullySubmitted ? undefined : "Partially Submitted"
      );
    });
  };

  handleClientServerUpdate = async (client, boolValue) => {
    let c = this.state.clients;
    let clientIndex = c.indexOf(client);
    c[clientIndex].serverUpdateInProgress = boolValue;
    this.setState({ clients: c });
  };

  confirmAlert = async () => {
    // Reset inactivity tracking
    this.lastActivityTime = Date.now();
    this._inactivityWarningVisible = false;
    this._warningStartTime = null;

    try {
      // Force token refresh
      const currentSession = await fetchAuthSession({ forceRefresh: true });
      if (!currentSession || !currentSession.tokens) {
        clearInterval(this.sessionCheckInterval);
        window.location = "/logout";
        return;
      }

      // Clear alert UI
      this.setState({
        sessionExpireNotificaiton: false,
        alertMessage: "",
        alertButtonMessage: "",
        confirmFunction: null,
        leftAlertDrawerOpen: false,
        warningRemainingSeconds: 0,
      });
    } catch (e) {
      console.log(`${e.message}: confirmAlert : Refresh failed, logging out`);
      clearInterval(this.sessionCheckInterval);
      window.location = "/logout";
    }
  };

  render() {
    let backdrop = undefined;
    let {
      background,
      sideDrawerOpen,
      clients,
      session,
      groups,
      serverRequests,
      financialProfessionals,
      currentFP,
      updatingFP,
      gettingClients,
    } = this.state;

    if (sideDrawerOpen) {
      backdrop = <BackDrop click={this.backdropClickHandler} />;
    }

    return (
      <div
        className={background}
        style={{ overflow: "auto" }}
        onScroll={this.handleScroll}
      >
        <div className="container-fluid">
          <div className="App mt-3">
            <main>
              <NavBar
                scrollHeight={this.GetScrollHeight}
                drawerClickHandler={this.drawerToggleClickHandler}
                GetScrollPosition={this.GetScrollPosition}
                session={session}
              />
              <SideDrawer
                user={session}
                show={sideDrawerOpen}
                newBlankProposal={this.newBlankProposal}
              />
              {backdrop}
              <LeftAlertDrawer
                show={this.state.leftAlertDrawerOpen}
                message={this.state.alertMessage}
                buttonMessage={this.state.alertButtonMessage}
                confrim={this.state.confirmFunction}
                remainingSeconds={this.state.warningRemainingSeconds}
                totalSeconds={120}
              />
              <Switch>
                <ProtectedRoute
                  exact
                  path="/"
                  render={(props) => (
                    <LandingPage
                      {...props}
                      groups={groups}
                      clients={clients}
                      gettingClients={gettingClients}
                    />
                  )}
                />
                <ProtectedRoute
                  path="/clients"
                  render={(props) => (
                    <Clients
                      {...props}
                      GetScrollPosition={this.GetScrollPosition}
                      scrollHeight={this.GetScrollHeight}
                      updatingFP={updatingFP}
                      currentFP={currentFP}
                      clients={clients}
                      groups={groups}
                      handleClientUpdate={this.handleClientUpdate}
                      handleAttestSubmit={this.handleClientAccountSubmissions}
                      submitClientUpdate={this.submitClientUpdate}
                      handleClientArchive={this.handleClientArchive}
                      serverRequests={serverRequests}
                      financialProfessionals={financialProfessionals}
                      getClients={this.getClients}
                      getClientsForMultipleFPs={this.getClientsForMultipleFPs}
                      clearClients={() => this.setState({ clients: [] })}
                    />
                  )}
                />
                <ProtectedRoute
                  path="/user"
                  render={(props) => <User {...props} />}
                />
                <Route
                  path="/login"
                  component={(props) => (
                    <Login
                      {...props}
                      handleBackgroundUpdate={this.handleBackgroundUpdate}
                    />
                  )}
                />
                <Route
                  path="/logout"
                  component={(props) => <Logout {...props} />}
                />
                <Route path="/forgot" component={ResetPassword} />
                <Route path="/not-found" component={NotFound} />
                <Redirect to="/not-found" />
              </Switch>
            </main>
          </div>
        </div>
      </div>
    );
  }
}
